<script setup lang="ts">
import { LucideInfo } from 'lucide-vue-next'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { useCurrencyFormat } from '~/composables/useCurrencyFormat'

const KGS_CURRENCY_ID = 1

const formSchema = toTypedSchema(
  z.object({
    amount: z
      .number({ message: useValidationMessage('requiredField') })
      .min(1, { message: useValidationMessage('minValueNumber', 1) }),
    period: z.array(z.number().min(1).max(60)),
    rate: z.array(z.number().min(0.5).max(25))
  })
)

const form = useForm({
  validationSchema: formSchema,
  initialValues: {
    amount: 10000,
    period: [12],
    rate: [10.0]
  }
})

const calculatedEarnings = computed(() => {
  const amount = form.values.amount || 0
  const rate = form.values.rate?.[0] || 0
  const months = form.values.period?.[0] || 1
  return (amount * rate * months) / (100 * 12)
})

const totalAmount = computed(() => {
  const amount = form.values.amount || 0
  const earnings = calculatedEarnings.value || 0
  return amount + earnings
})

type SliderFieldUpdate = (value: number[] | undefined) => void

const handleSliderUpdate = (
  componentField: { 'onUpdate:modelValue'?: SliderFieldUpdate },
  value: number[] | undefined
) => {
  componentField['onUpdate:modelValue']?.(value)
}
</script>

<template>
  <div class="flex flex-col items-center gap-6 md:flex-row">
    <div class="flex w-full flex-col gap-5 md:min-w-[400px]">
      <FormField v-slot="{ componentField }" name="amount">
        <FormItem>
          <FormLabel>{{ $t('widget.calculators.labels.deposit_amount') }}</FormLabel>
          <FormControl>
            <Input type="number" :min="1" v-bind="componentField" />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="period">
        <FormItem class="space-y-3">
          <FormLabel>{{ $t('feature.deposit_calculator.labels.deposit_term_months') }}</FormLabel>
          <FormControl>
            <Slider
              :model-value="componentField.modelValue"
              :max="60"
              :min="1"
              :step="1"
              :aria-label-name="$t('feature.deposit_calculator.labels.deposit_term_months')"
              :name="componentField.name"
              @update:model-value="(value) => handleSliderUpdate(componentField, value)"
            />
            <FormDescription class="flex justify-between text-gray-500">
              <span>1 {{ $t('common.months_short') }}.</span>
              <span>{{ form.values.period?.[0] || 1 }} {{ $t('common.months_short') }}.</span>
              <span>60 {{ $t('common.months_short') }}.</span>
            </FormDescription>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="rate">
        <FormItem class="space-y-3">
          <FormLabel>{{
            $t('feature.deposit_general_calculator.labels.interest_rate_annual')
          }}</FormLabel>
          <FormControl>
            <Slider
              :model-value="componentField.modelValue"
              :max="25"
              :min="0.5"
              :step="0.5"
              :name="componentField.name"
              :aria-label-name="
                $t('feature.deposit_general_calculator.labels.interest_rate_annual')
              "
              @update:model-value="(value) => handleSliderUpdate(componentField, value)"
            />
            <FormDescription class="flex justify-between text-gray-500">
              <span>0.5%</span>
              <span>{{ (form.values.rate?.[0] || 0.5).toFixed(1) }}%</span>
              <span>25%</span>
            </FormDescription>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <div class="hidden items-center gap-2.5 text-sm md:flex">
        <LucideInfo class="min-w-5 text-yellow-400" />
        {{ $t('feature.deposit_calculator.disclaimer') }}
      </div>
    </div>

    <div class="flex w-full flex-col items-center gap-4 md:text-center">
      <div class="text-center">
        <div class="mb-2 text-gray-600">
          {{ $t('widget.card_page.info_items.card_interest_rate') }}
        </div>
        <div class="mb-4 text-2xl font-bold text-blue-600">
          {{ (form.values.rate?.[0] || 0).toFixed(2) }}%
        </div>
      </div>

      <div class="text-center">
        <div class="mb-2 text-gray-600">
          {{ $t('feature.deposit_calculator.results.your_income') }}
        </div>
        <div class="mb-4 text-2xl font-bold text-green-600">
          {{ useCurrencyFormat(calculatedEarnings || 0, KGS_CURRENCY_ID) }}
        </div>
      </div>

      <div class="text-center">
        <div class="mb-2 text-gray-600">
          {{ $t('feature.deposit_calculator.results.total_amount_receivable') }}
        </div>
        <div class="text-3xl font-bold text-blue-900">
          {{ useCurrencyFormat(totalAmount || 0, KGS_CURRENCY_ID) }}
        </div>
      </div>

      <div class="text-center">
        <div class="text-sm">
          {{ $t('feature.deposit_general_calculator.cta.ready_for_next_step') }}
        </div>
        <Button as-child class="mt-2">
          <NuxtLinkLocale to="/deposits">{{
            $t('feature.deposit_general_calculator.cta.find_deposits')
          }}</NuxtLinkLocale>
        </Button>
      </div>

      <div class="mt-4 flex items-center gap-2.5 text-sm md:hidden">
        <LucideInfo class="min-w-5 text-yellow-400" />
        {{ $t('feature.deposit_calculator.disclaimer') }}
      </div>
    </div>
  </div>
</template>
